// EventCheckinScreen.jsx - Mobile-optimized check-in interface
import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  CheckCircle, 
  Circle, 
  Users, 
  Clock, 
  TrendingUp,
  RefreshCw,
  User,
  UserCheck,
  X,
  ChevronRight,
  Activity,
  Wifi,
  WifiOff
} from 'lucide-react';
import { EventbriteApiService, AttendeeUtils } from '../utils/eventApiHelpers';

const EventCheckinScreen = ({ eventId, eventDetails, userToken }) => {
  const [attendees, setAttendees] = useState([]);
  const [filteredAttendees, setFilteredAttendees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingActions, setPendingActions] = useState([]);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  
  const searchRef = useRef(null);
  const apiService = new EventbriteApiService(userToken);

  // Load attendees on mount
  useEffect(() => {
    loadAttendees();
    
    // Set up online/offline detection
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [eventId, eventDetails]);

  // Process pending actions when back online
  useEffect(() => {
    if (isOnline && pendingActions.length > 0) {
      processPendingActions();
    }
  }, [isOnline]);

  // Filter attendees based on search
  useEffect(() => {
    console.log('Filtering attendees. Total attendees:', attendees.length);
    if (!searchQuery.trim()) {
      // Show recently checked in first, then unchecked
      const sorted = [...attendees].sort((a, b) => {
        const aCheckedIn = a.tickets.some(t => t.checked_in);
        const bCheckedIn = b.tickets.some(t => t.checked_in);
        
        if (aCheckedIn && !bCheckedIn) return -1;
        if (!aCheckedIn && bCheckedIn) return 1;
        return 0;
      });
      console.log('Sorted attendees to display:', sorted.length);
      setFilteredAttendees(sorted); // Show all attendees, no pagination limit
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = attendees.filter(attendee =>
        attendee.attendee_first_name.toLowerCase().includes(query) ||
        attendee.attendee_last_name.toLowerCase().includes(query) ||
        attendee.attendee_email.toLowerCase().includes(query) ||
        (attendee.handle && attendee.handle.toLowerCase().includes(query))
      );
      console.log('Filtered attendees for search:', filtered.length);
      setFilteredAttendees(filtered);
    }
  }, [searchQuery, attendees]);

  // Update stats when attendees change
  useEffect(() => {
    if (attendees.length > 0) {
      updateStats(attendees);
    }
  }, [attendees]);

  const loadAttendees = async () => {
    console.log('loadAttendees called with eventDetails:', eventDetails);
    if (!eventDetails?.eventbrite_id) {
      console.log('No eventbrite_id found in eventDetails');
      return;
    }
    
    console.log('Loading attendees for eventbrite_id:', eventDetails.eventbrite_id);
    setLoading(true);
    try {
      const data = await apiService.getEventAttendees(eventDetails.eventbrite_id, {
        limitVal: 1000 // Load all attendees for check-in screen
      });
      
      console.log('Attendees data received:', data);
      console.log('Total attendees loaded:', data.attendees?.length || 0);
      setAttendees(data.attendees || []);
      updateStats(data.attendees || []);
    } catch (error) {
      console.error('Failed to load attendees:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (attendeeList) => {
    const newStats = AttendeeUtils.calculateStats(attendeeList);
    setStats(newStats);
    
    // Update recent check-ins (last 10)
    const recent = attendeeList
      .filter(a => a.tickets.some(t => t.checked_in))
      .slice(-10)
      .reverse();
    setRecentCheckins(recent);
  };

  const handleCheckin = async (attendee, ticketId) => {
    const ticket = attendee.tickets.find(t => t.ticket_id === ticketId);
    const newStatus = !ticket.checked_in;
    
    if (!isOnline) {
      // Queue for later when online
      setPendingActions(prev => [...prev, {
        attendee,
        ticketId,
        newStatus,
        timestamp: Date.now()
      }]);
      
      // Update UI optimistically
      updateAttendeeLocally(attendee.attendee_email, ticketId, newStatus);
      return;
    }

    try {
      await apiService.updateAttendeeCheckin(
        eventDetails.eventbrite_id, 
        ticketId, 
        newStatus
      );
      
      // Update local state
      updateAttendeeLocally(attendee.attendee_email, ticketId, newStatus);
      
      // Show success feedback
      showCheckinFeedback(attendee, newStatus);
      
    } catch (error) {
      console.error('Check-in failed:', error);
      alert(`Failed to ${newStatus ? 'check in' : 'check out'} ${attendee.attendee_first_name}`);
    }
  };

  const updateAttendeeLocally = (email, ticketId, checkedIn) => {
    setAttendees(prev => prev.map(attendee => {
      if (attendee.attendee_email === email) {
        return {
          ...attendee,
          tickets: attendee.tickets.map(ticket =>
            ticket.ticket_id === ticketId
              ? { ...ticket, checked_in: checkedIn }
              : ticket
          )
        };
      }
      return attendee;
    }));
  };

  const showCheckinFeedback = (attendee, checkedIn) => {
    // Create floating feedback message
    const message = document.createElement('div');
    message.className = `checkin-feedback ${checkedIn ? 'success' : 'undo'}`;
    message.textContent = `${attendee.attendee_first_name} ${checkedIn ? 'checked in' : 'checked out'}!`;
    document.body.appendChild(message);
    
    setTimeout(() => {
      document.body.removeChild(message);
    }, 2000);
  };

  const processPendingActions = async () => {
    for (const action of pendingActions) {
      try {
        await apiService.updateAttendeeCheckin(
          eventDetails.eventbrite_id,
          action.ticketId,
          action.newStatus
        );
      } catch (error) {
        console.error('Failed to process pending action:', error);
      }
    }
    setPendingActions([]);
  };

  const syncWithEventbrite = async () => {
    setLoading(true);
    try {
      await apiService.syncEventAttendees(eventDetails.eventbrite_id);
      await loadAttendees();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkin-screen">
      {/* Header */}
      <div className="checkin-header">
        <div className="event-info">
          <h1>{eventDetails?.name || 'Event Check-in'}</h1>
          <div className="event-meta">
            <span className="event-date">
              {eventDetails?.event_start && new Date(eventDetails.event_start).toLocaleDateString()}
            </span>
            <div className="connection-status">
              {isOnline ? (
                <><Wifi className="status-icon online" /> Online</>
              ) : (
                <><WifiOff className="status-icon offline" /> Offline</>
              )}
              {pendingActions.length > 0 && (
                <span className="pending-count">({pendingActions.length} pending)</span>
              )}
            </div>
          </div>
        </div>
        
        <button onClick={syncWithEventbrite} className="sync-button" disabled={loading}>
          <RefreshCw className={`sync-icon ${loading ? 'spinning' : ''}`} />
        </button>
      </div>

      {/* Stats Dashboard */}
      <div className="stats-dashboard">
        <div className="stat-card primary">
          <div className="stat-number">{stats.checkedInTickets || 0}</div>
          <div className="stat-label">Checked In</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.totalTickets || 0}</div>
          <div className="stat-label">Total Tickets</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.checkInPercentage || 0}%</div>
          <div className="stat-label">Attendance</div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-bar">
          <Search className="search-icon" />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="clear-search"
            >
              <X className="clear-icon" />
            </button>
          )}
        </div>
      </div>

      {/* Attendee List */}
      <div className="attendee-list">
        {loading ? (
          <div className="loading-message">
            <RefreshCw className="spinning" />
            <span>Loading attendees...</span>
          </div>
        ) : filteredAttendees.length === 0 ? (
          <div className="no-results">
            {searchQuery ? 'No attendees found' : 'No attendees loaded'}
          </div>
        ) : (
          filteredAttendees.map((attendee) => (
            <AttendeeCheckinCard
              key={attendee.attendee_email}
              attendee={attendee}
              onCheckin={handleCheckin}
              onSelect={setSelectedAttendee}
            />
          ))
        )}
      </div>

      {/* Selected Attendee Modal */}
      {selectedAttendee && (
        <AttendeeDetailModal
          attendee={selectedAttendee}
          onClose={() => setSelectedAttendee(null)}
          onCheckin={handleCheckin}
        />
      )}

      {/* Recent Activity Sidebar (Desktop) */}
      <div className="recent-activity">
        <h3>Recent Check-ins</h3>
        {recentCheckins.slice(0, 5).map((attendee, index) => (
          <div key={`${attendee.attendee_email}-${index}`} className="recent-item">
            <div className="recent-avatar">
              {attendee.profile_img ? (
                <img src={attendee.profile_img} alt="" />
              ) : (
                <User className="default-avatar" />
              )}
            </div>
            <div className="recent-info">
              <div className="recent-name">
                {attendee.attendee_first_name} {attendee.attendee_last_name}
              </div>
              <div className="recent-time">Just now</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Individual Attendee Card for Check-in
const AttendeeCheckinCard = ({ attendee, onCheckin, onSelect }) => {
  const hasCheckedIn = attendee.tickets.some(t => t.checked_in);
  const allCheckedIn = attendee.tickets.every(t => t.checked_in);
  
  return (
    <div className={`attendee-card ${hasCheckedIn ? 'checked-in' : 'not-checked-in'}`}>
      <div className="attendee-main" onClick={() => onSelect(attendee)}>
        <div className="attendee-avatar">
          {attendee.profile_img ? (
            <img src={attendee.profile_img} alt="" />
          ) : (
            <div className="default-avatar">
              {attendee.attendee_first_name[0]}{attendee.attendee_last_name[0]}
            </div>
          )}
          {attendee.is_in_system && (
            <div className="user-badge">
              <UserCheck className="badge-icon" />
            </div>
          )}
        </div>
        
        <div className="attendee-info">
          <div className="attendee-name">
            {attendee.attendee_first_name} {attendee.attendee_last_name}
          </div>
          <div className="attendee-details">
            <span className="attendee-email">{attendee.attendee_email}</span>
            {attendee.handle && (
              <span className="attendee-handle">@{attendee.handle}</span>
            )}
          </div>
          <div className="ticket-info">
            {attendee.num_tickets} ticket{attendee.num_tickets !== 1 ? 's' : ''} • {attendee.ticket_tier}
          </div>
        </div>
        
        <ChevronRight className="expand-icon" />
      </div>
      
      {/* Quick Check-in Buttons */}
      <div className="checkin-actions">
        {attendee.tickets.map(ticket => (
          <button
            key={ticket.ticket_id}
            onClick={(e) => {
              e.stopPropagation();
              onCheckin(attendee, ticket.ticket_id);
            }}
            className={`checkin-btn ${ticket.checked_in ? 'checked-in' : 'check-in'}`}
          >
            {ticket.checked_in ? (
              <>
                <CheckCircle className="btn-icon" />
                Checked In
              </>
            ) : (
              <>
                <Circle className="btn-icon" />
                Check In
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// Detailed Modal for Multi-ticket Attendees
const AttendeeDetailModal = ({ attendee, onClose, onCheckin }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{attendee.attendee_first_name} {attendee.attendee_last_name}</h2>
          <button onClick={onClose} className="close-btn">
            <X className="close-icon" />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="attendee-profile">
            {attendee.profile_img ? (
              <img src={attendee.profile_img} alt="" className="profile-image" />
            ) : (
              <div className="default-profile">
                {attendee.attendee_first_name[0]}{attendee.attendee_last_name[0]}
              </div>
            )}
            
            <div className="profile-info">
              <div className="profile-email">{attendee.attendee_email}</div>
              {attendee.handle && (
                <div className="profile-handle">@{attendee.handle}</div>
              )}
              {attendee.is_in_system && (
                <div className="profile-badge">
                  <UserCheck className="badge-icon" />
                  VibrantCast User
                </div>
              )}
            </div>
          </div>
          
          <div className="tickets-section">
            <h3>Tickets ({attendee.num_tickets})</h3>
            {attendee.tickets.map((ticket, index) => (
              <div key={ticket.ticket_id} className="ticket-item">
                <div className="ticket-info">
                  <span className="ticket-label">Ticket {index + 1}</span>
                  <span className="ticket-tier">{attendee.ticket_tier}</span>
                </div>
                <button
                  onClick={() => onCheckin(attendee, ticket.ticket_id)}
                  className={`ticket-checkin-btn ${ticket.checked_in ? 'checked-in' : 'check-in'}`}
                >
                  {ticket.checked_in ? (
                    <>
                      <CheckCircle className="btn-icon" />
                      Checked In
                    </>
                  ) : (
                    <>
                      <Circle className="btn-icon" />
                      Check In
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCheckinScreen;